export interface JsonTextError {
  message: string;
  /** 1-based; null when the engine does not report a position. */
  line: number | null;
  column: number | null;
}

export type JsonTextResult =
  | { ok: true; value: Record<string, unknown> | null }
  | { ok: false; error: JsonTextError };

/** Line and column of a 0-based character offset in `text`. */
const positionOf = (text: string, offset: number) => {
  const before = text.slice(0, offset).split("\n");
  return { line: before.length, column: before[before.length - 1].length + 1 };
};

/**
 * Parse an editor's JSON text without throwing. Empty text is a valid "no
 * value" (null). A value that is not an object is rejected, since every JSON
 * field the flow config edits is a JSON-schema object.
 */
export const parseJsonText = (text: string): JsonTextResult => {
  if (!text.trim()) return { ok: true, value: null };
  try {
    const value: unknown = JSON.parse(text);
    if (value === null || typeof value !== "object" || Array.isArray(value)) {
      return { ok: false, error: { message: "Expected a JSON object", line: 1, column: 1 } };
    }
    return { ok: true, value: value as Record<string, unknown> };
  } catch (err) {
    const raw = err instanceof Error ? err.message : String(err);
    // The position is reported separately; keep only the human part of the message.
    const message = raw.replace(/\s*in JSON at position \d+.*$/, "").replace(/\s*\(line \d+ column \d+\)$/, "");
    // V8: "... in JSON at position 42" (older) or "(line 3 column 5)" (newer).
    const lineColumn = /line (\d+) column (\d+)/.exec(raw);
    if (lineColumn) {
      return { ok: false, error: { message, line: Number(lineColumn[1]), column: Number(lineColumn[2]) } };
    }
    const position = /position (\d+)/.exec(raw);
    if (position) {
      return { ok: false, error: { message, ...positionOf(text, Number(position[1])) } };
    }
    return { ok: false, error: { message, line: null, column: null } };
  }
};
